import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import "../FormStyles.css";
import "./Competition.css";
import useAxios from "../useAxios";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Select from "react-select";
import CompetitionKnockout from "./Knockout/CompetitionKnockout";
import CompetitionRoundRobin from "./RoundRobin/CompetitionRoundRobin";

const Competition = () => {
  const { id } = useParams();
  const knockoutRef = useRef();
  const roundRobinRef = useRef();

  const [axios] = useAxios(true);
  const navigate = useNavigate();

  const [players, setPlayers] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [gameTypes, setGameTypes] = useState([]);

  const [types] = useState([
    { id: "knockout", name: "Knockout" },
    { id: "roundrobin", name: "Round Robin" },
  ]);

  useEffect(() => {
    axios.get("players", setPlayers);
    axios.get("competition/" + id, setCompetition);
    axios.get("gametypes", setGameTypes);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompetition((prevcomp) => ({ ...prevcomp, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCompetition((prevcomp) => ({ ...prevcomp, [name]: checked }));
  };

  const handleDateTimeChange = (datetime) => {
    setCompetition((prevcomp) => ({ ...prevcomp, dateTime: datetime }));
  };

  const handleTypeChange = (type) => {
    setCompetition((prevcomp) => ({ ...prevcomp, type: type }));
  };

  const handleGameTypeChange = (e) => {
    const { value } = e.target;
    const gameType = gameTypes.find((gt) => gt.id === parseInt(value));
    setCompetition((prevcomp) => ({
      ...prevcomp,
      gameType: gameType || null,
    }));
  };

  const onSave = () => {
    axios.post("competition/" + id, competition, saveTypeSpecifics);
  };

  const saveTypeSpecifics = (response) => {
    if (response?.type?.id === "knockout" && knockoutRef.current !== null) {
      knockoutRef.current.onSave(response, onCancel);
    }

    if (response?.type?.id === "roundrobin" && roundRobinRef.current !== null) {
      roundRobinRef.current.onSave(response, onCancel);
    }
  };

  const onCancel = () => {
    navigate("/competitions");
  };

  if (competition === null || players === null) {
    return <h1>Loading</h1>;
  }

  return (
    <div className="edit-competition-container">
      <h2>Edit Competition</h2>
      <form className="competition-form-fields">
        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            name="name"
            id="name"
            value={competition.name}
            onChange={handleChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="dateTime">Date/Time:</label>
          <DatePicker
            selected={new Date(competition.dateTime)}
            onChange={handleDateTimeChange}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="dd MMM yyyy HH:mm"
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="sameDay">Same Day:</label>
          <input
            type="checkbox"
            name="sameDay"
            id="sameDay"
            checked={competition.sameDay ?? true}
            onChange={handleCheckboxChange}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="gameType">Game Type:</label>
          <select
            name="gameType"
            id="gameType"
            value={competition.gameType?.id || ""}
            onChange={handleGameTypeChange}
            className="form-control"
            disabled={id !== "new" && competition.status !== 0}
          >
            <option value="">Select a game type</option>
            {gameTypes.map((gameType) => (
              <option key={gameType.id} value={gameType.id}>
                {gameType.description}
              </option>
            ))}
          </select>
        </div>
        {competition.gameType && (
          <>
            <div className="form-group">
              <label htmlFor="type">Competition Type:</label>
              {id === "new" ? (
                <Select
                  name="type"
                  value={competition.type}
                  onChange={(option) => handleTypeChange(option)}
                  options={types}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  isSearchable={false}
                  className="form-control"
                />
              ) : (
                <span>{competition.type.name}</span>
              )}
            </div>
            <div style={{ marginBottom: 24 }}>
              {competition.type.id === "knockout" ? (
                <CompetitionKnockout ref={knockoutRef} competition={competition} id={id} />
              ) : competition.type.id === "roundrobin" ? (
                <CompetitionRoundRobin
                  ref={roundRobinRef}
                  competition={competition}
                  id={id}
                />
              ) : null}
            </div>
          </>
        )}
        <div className="form-buttons">
          <button
            type="button"
            onClick={onSave}
            disabled={competition.status !== 0 || !competition.name || !competition.gameType || !competition.type}
            className="submit-button"
          >
            Save
          </button>
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default Competition;
